import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { notifyApiError, type ErrorMessages } from "../../lib/toast-error";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useMasterData } from "../master-data/api";
import { useUpdateIncident, type IncidentResponse } from "./api";
import {
  buildIncidentPatch,
  draftFromIncident,
  editProblems,
  MAX_TITLE_CHARS,
  selectableEntries,
  type IncidentEditDraft,
  type IncidentEditProblem,
} from "./edit";

const PROBLEM_KEY: Record<IncidentEditProblem, string> = {
  titleRequired: "create.validation.titleRequired",
  titleTooLong: "create.validation.titleMax",
  descriptionRequired: "create.validation.descriptionRequired",
  areasRequired: "create.validation.areasRequired",
};

/**
 * The edit dialog for an incident's own fields (`incident.edit`, [F-001]):
 * title, description, category, severity and areas. Shown only where the
 * incident's `can` holds the operation; the server re-checks it, and its
 * If-Match turns a concurrent change into the "someone was faster" notice.
 * The dialog is keyed on the incident version by its caller, so it always
 * opens on the current values.
 */
export function IncidentEditDialog({
  incident,
  open,
  onClose,
  onStale,
}: {
  incident: IncidentResponse;
  open: boolean;
  onClose: () => void;
  /** Called after a 412, so the page reloads the incident. */
  onStale: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const master = useMasterData();
  const update = useUpdateIncident(incident.id);
  const [draft, setDraft] = useState<IncidentEditDraft>(() => draftFromIncident(incident));

  const errorMessages: ErrorMessages = {
    precondition: t("errors.precondition"),
    forbidden: t("errors.forbidden"),
    conflict: t("errors.conflict"),
    generic: t("errors.generic"),
  };

  const problems = editProblems(draft);
  const patch = buildIncidentPatch(incident, draft);
  const categories = selectableEntries(master.categories.data, [incident.categoryId]);
  const severities = selectableEntries(master.severities.data, [incident.severityId]);
  const areas = selectableEntries(master.areas.data, incident.areaIds);

  const set = <K extends keyof IncidentEditDraft>(key: K, value: IncidentEditDraft[K]): void =>
    setDraft((current) => ({ ...current, [key]: value }));

  const onSave = (): void => {
    if (problems.length > 0) return;
    if (!patch) {
      onClose();
      return;
    }
    update.mutate(
      { body: patch, version: incident.version },
      {
        onSuccess: () => {
          onClose();
          toast.success(t("detail.edit.saved"));
        },
        onError: (error) => notifyApiError(error, errorMessages, onStale),
      },
    );
  };

  const inactiveSuffix = (active: boolean): string =>
    active ? "" : ` ${t("detail.edit.inactiveSuffix")}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        data-testid="incident-edit_dialog"
        className="max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{t("detail.edit.title")}</DialogTitle>
          <DialogDescription>{t("detail.edit.intro")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="incident-edit-title">{t("create.fieldTitle")}</Label>
            <Input
              id="incident-edit-title"
              data-testid="incident-edit_title"
              value={draft.title}
              maxLength={MAX_TITLE_CHARS + 50}
              onChange={(event) => set("title", event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="incident-edit-description">{t("detail.description")}</Label>
            <Textarea
              id="incident-edit-description"
              data-testid="incident-edit_description"
              rows={5}
              value={draft.description}
              onChange={(event) => set("description", event.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="incident-edit-category">{t("detail.category")}</Label>
              <Select value={draft.categoryId} onValueChange={(value) => set("categoryId", value)}>
                <SelectTrigger id="incident-edit-category" data-testid="incident-edit_category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                      {inactiveSuffix(category.active)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="incident-edit-severity">{t("detail.severity")}</Label>
              <Select value={draft.severityId} onValueChange={(value) => set("severityId", value)}>
                <SelectTrigger id="incident-edit-severity" data-testid="incident-edit_severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {severities.map((severity) => (
                    <SelectItem key={severity.id} value={severity.id}>
                      {severity.name}
                      {inactiveSuffix(severity.active)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <span id="incident-edit-areas-label" className="text-sm font-medium">
              {t("detail.areas")}
            </span>
            <div
              role="group"
              aria-labelledby="incident-edit-areas-label"
              data-testid="incident-edit_areas"
              className="flex flex-wrap gap-2"
            >
              {areas.map((area) => {
                const selected = draft.areaIds.includes(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    data-testid={`incident-edit_area-option_${area.id}`}
                    aria-pressed={selected}
                    onClick={() =>
                      set(
                        "areaIds",
                        selected
                          ? draft.areaIds.filter((id) => id !== area.id)
                          : [...draft.areaIds, area.id],
                      )
                    }
                    className={cn(
                      "inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors sm:min-h-9",
                      selected
                        ? "border-primary-text bg-primary text-primary-foreground"
                        : "border-input bg-background text-foreground hover:bg-accent",
                    )}
                  >
                    {selected ? <Check aria-hidden="true" className="h-4 w-4" /> : null}
                    {area.name}
                    {inactiveSuffix(area.active)}
                  </button>
                );
              })}
            </div>
          </div>

          {problems.length > 0 ? (
            <ul
              data-testid="incident-edit_problems"
              className="space-y-1 text-sm text-destructive-text"
            >
              {problems.map((problem) => (
                <li key={problem}>{t(PROBLEM_KEY[problem])}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="incident-edit_cancel"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="incident-edit_save"
            className="h-11 sm:h-9"
            disabled={problems.length > 0 || update.isPending}
            onClick={onSave}
          >
            {update.isPending ? t("detail.edit.saving") : t("detail.edit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
